/**
 * ContactCard — Guard's official contact block.
 *
 * Sits below the CompanyAlbum on the LoginPage and (eventually) any
 * marketing page. Mirrors the legal contact info already in the Terms
 * (§ 16) and Technical Paper so a visitor doesn't have to dig through
 * a long policy doc to find an email or the donations account.
 *
 * Why the registered office is NOT shown here: by request (Feb 13, 2026),
 * to reduce doxxing / physical-targeting risk against the operator's
 * staff. Anyone who legitimately needs the postal address (CAC filings,
 * tax authorities, formal legal service) can request it via the data-
 * request mailbox below — that path keeps the address out of casual web
 * scrapers while preserving statutory access.
 */
import { Mail, Building2, Banknote, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

export default function ContactCard() {
  return (
    <section
      className="bg-[#0F172A] text-white px-6 sm:px-12 py-14"
      data-testid="contact-card"
      aria-labelledby="contact-heading"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="w-5 h-5 text-red-400" strokeWidth={2} />
          <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-red-400 font-body">
            Get in touch
          </span>
        </div>
        <h2
          id="contact-heading"
          className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3"
        >
          Contrivances Engineering Ent. Ltd.
        </h2>
        <p className="font-body text-sm sm:text-base text-gray-300 max-w-2xl mb-10">
          Operator of the Guard Emergency Alert Network, ePass premise control, and the public stolen-vehicle lookup.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
          {/* Email */}
          <div
            className="border border-white/10 rounded-sm p-5 bg-white/5"
            data-testid="contact-email"
          >
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-red-400" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-red-400">
                Email us
              </span>
            </div>
            <a
              href="mailto:alerts@contritrack.com"
              className="font-body text-base font-bold text-white hover:underline break-all"
            >
              alerts@contritrack.com
            </a>
            <p className="text-[11px] text-gray-400 font-body mt-2">
              Operations, ePass enrolment, government on-boarding, postal-address requests, and
              data subject requests (subject line: <em>Data Request — Guard</em>).
            </p>
          </div>

          {/* Donation account */}
          <div
            className="border border-white/10 rounded-sm p-5 bg-white/5"
            data-testid="contact-donation"
          >
            <div className="flex items-center gap-2 mb-3">
              <Banknote className="w-4 h-4 text-red-400" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-red-400">
                Donations
              </span>
            </div>
            <p className="font-body text-sm text-gray-100">
              <span className="font-bold">GTBank</span>
              <span className="block font-mono text-base mt-0.5">0035453902</span>
            </p>
            <p className="text-[11px] text-gray-400 font-body mt-2">
              Account name: Contrivances Engineering Ent. Ltd. — or pay online via Flutterwave.
            </p>
            <Link
              to="/donate"
              className="mt-3 inline-flex items-center gap-1 text-[11px] text-red-300 hover:text-red-200 font-bold"
            >
              Donate now <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>

        <p className="text-[11px] text-gray-500 font-body mt-10 text-center">
          &copy; {new Date().getFullYear()} Contrivances Engineering Ent. Ltd. — Guard is a service mark of CEEL.
        </p>
      </div>
    </section>
  );
}
